import type Alpine from 'alpinejs';

// This engine adapts behavior and matching ideas from Alpine Morph, Morphlex, Idiomorph, and Datastar.
// See THIRD_PARTY_NOTICES.md for provenance and license notices.

type Skip = () => void;
type ChildrenOnly = () => void;
type SkipChildren = () => void;
type SkipUntil = (predicate: (node: Node) => boolean) => void;
type UpdatingHook = (
    from: Node,
    to: Node,
    childrenOnly: ChildrenOnly,
    skip: Skip,
    skipChildren: SkipChildren,
    skipUntil: SkipUntil,
) => void;
type SkippableNodeHook = (node: Node, skip: Skip) => void;
type PruningPolicy = 'after-root-hook' | 'before-descendant-hook';

type MorphOptions = {
    key?: (element: Element) => unknown;
    lookahead?: boolean;
    updating?: UpdatingHook;
    updated?: (from: Node, to: Node) => void;
    removing?: SkippableNodeHook;
    removed?: (node: Node) => void;
    adding?: SkippableNodeHook;
    added?: (node: Node) => void;
};

type MorphAlpine = Omit<Alpine.Alpine, 'cloneNode' | 'closestDataStack'> & {
    cloneNode(from: Element, to: Element): void;
    closestDataStack(node: Node): unknown;
    morph(from: Element, toHtml: Element | string, options?: MorphOptions): Element;
    morphBetween(
        startMarker: Comment,
        endMarker: Comment,
        toHtml: Element | string,
        options?: MorphOptions,
    ): void;
};

type AlpineNode = Node & {
    _x_bindings?: {id?: string};
    _x_dataStack?: unknown;
    _x_currentIfEl?: AlpineNode;
    _x_lastRenderedEl?: AlpineNode;
    _x_lookup?: Map<unknown, AlpineNode>;
    _x_teleport?: AlpineNode;
    _x_transitioning?: unknown;
    _x_isShown?: boolean;
};

type Parent = Node & {
    children: Iterable<Element>;
    firstChild: ChildNode | null;
    insertBefore(node: Node, before: Node | null): Node;
};

const noop = (): void => {};
let alpineInstance: MorphAlpine | undefined;

class Block {
    constructor(
        readonly startComment: Comment,
        readonly endComment: Comment,
    ) {}

    get children(): Element[] {
        const children: Element[] = [];
        let node = this.startComment.nextSibling;
        while (node && node !== this.endComment) {
            if (node instanceof Element) children.push(node);
            node = node.nextSibling;
        }
        return children;
    }

    get firstChild(): ChildNode | null {
        const first = this.startComment.nextSibling;
        return first === this.endComment ? null : first;
    }

    appendChild(node: Node): Node {
        this.endComment.before(node);
        return node;
    }

    insertBefore(node: Node, reference: Node | null): Node {
        ((reference ?? this.endComment) as ChildNode).before(node);
        return node;
    }

    nextNode(reference: Node): ChildNode | null {
        const next = reference.nextSibling;
        return next === this.endComment ? null : next;
    }
}

const nextSibling = (parent: Parent | Block, node: Node): ChildNode | null => (
    parent instanceof Block ? parent.nextNode(node) : node.nextSibling
);

const createElement = (html: string): Element => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const element = template.content.firstElementChild;
    if (!element) throw new Error('Morph HTML must contain a root element.');
    return element;
};

const setIncomingAttribute = (target: Element, incoming: Attr): void => {
    if (incoming.namespaceURI) {
        target.setAttributeNodeNS(incoming.cloneNode() as Attr);
    } else if (incoming.name.includes('@')) {
        target.setAttributeNode(incoming.cloneNode() as Attr);
    } else {
        target.setAttribute(incoming.name, incoming.value);
    }
};

const shouldSkip = (hook: SkippableNodeHook, node: Node): boolean => {
    let skip = false;
    hook(node, () => { skip = true; });
    return skip;
};

const isConditionalStart = (node: Node | null): boolean => (
    node?.nodeType === Node.COMMENT_NODE && node.textContent === '[if BLOCK]><![endif]'
);

const isConditionalEnd = (node: Node | null): boolean => (
    node?.nodeType === Node.COMMENT_NODE && node.textContent === '[if ENDBLOCK]><![endif]'
);

const conditionalEnd = (parent: Parent | Block, start: Node): Comment => {
    let depth = 0;
    let node: Node | null = start;
    while ((node = nextSibling(parent, node))) {
        if (isConditionalStart(node)) depth += 1;
        else if (isConditionalEnd(node) && depth > 0) depth -= 1;
        else if (isConditionalEnd(node)) return node as Comment;
    }
    throw new Error('Unclosed conditional morph block.');
};

type OwnedRange = {
    start: AlpineNode;
    end: AlpineNode;
};

const ownedRange = (start: AlpineNode): OwnedRange => {
    let end = start;
    if (start._x_lastRenderedEl) end = start._x_lastRenderedEl;
    else if (start._x_currentIfEl) end = ownedRange(start._x_currentIfEl).end;
    else if (start._x_lookup?.size) {
        const owned = new Set(start._x_lookup.values());
        let sibling = start.nextSibling as AlpineNode | null;
        while (sibling && owned.has(sibling)) {
            end = ownedRange(sibling).end;
            sibling = end.nextSibling as AlpineNode | null;
        }
    }
    return {start, end};
};

const afterOwnedRange = (parent: Parent | Block, range: OwnedRange): AlpineNode | null => (
    nextSibling(parent, range.end) as AlpineNode | null
);

const isServerEqual = (from: AlpineNode, to: AlpineNode): boolean => {
    if (from.nodeType !== to.nodeType || from.nodeName !== to.nodeName) return false;
    if (from.nodeType === Node.TEXT_NODE || from.nodeType === Node.COMMENT_NODE) {
        return from.nodeValue === to.nodeValue;
    }
    if (from instanceof HTMLTemplateElement && to instanceof HTMLTemplateElement) {
        const fromTemplate = from as HTMLTemplateElement & AlpineNode;
        const toTemplate = to as HTMLTemplateElement & AlpineNode;
        if (fromTemplate._x_teleport) {
            const incomingTeleport = toTemplate._x_teleport ?? to.content.firstElementChild;
            return Boolean(incomingTeleport && isServerEqual(fromTemplate._x_teleport, incomingTeleport as AlpineNode));
        }
        return from.isEqualNode(to);
    }
    if (from instanceof Element && to instanceof Element) {
        if (from.attributes.length !== to.attributes.length) return false;
        for (const {name, value} of from.attributes) {
            if (to.getAttribute(name) !== value) return false;
        }
    }

    let fromChild = from.firstChild as AlpineNode | null;
    let toChild = to.firstChild as AlpineNode | null;
    while (fromChild && toChild) {
        if (!isServerEqual(fromChild, toChild)) return false;
        fromChild = ownedRange(fromChild).end.nextSibling as AlpineNode | null;
        toChild = toChild.nextSibling as AlpineNode | null;
    }
    return fromChild === null && toChild === null;
};

const moveOwnedRangeBefore = (parent: Parent | Block, range: OwnedRange, before: Node | null): void => {
    if (range.start === before || afterOwnedRange(parent, range) === before) return;
    let node: Node | null = range.start;
    while (node) {
        const next: ChildNode | null = node.nextSibling;
        parent.insertBefore(node, before);
        if (node === range.end) break;
        node = next;
    }
};

const seedDynamicId = (to: AlpineNode, from: AlpineNode | null): void => {
    const id = from?._x_bindings?.id;
    if (!id || !(to instanceof Element)) return;
    to.setAttribute('id', id);
    (to as HTMLElement).id = id;
};

const buildDescendantCorrespondence = (
    fromElements: Element[],
    toElements: Element[],
    getKey: (node: Node | null) => unknown,
): WeakMap<Node, symbol> => {
    const correspondence = new WeakMap<Node, symbol>();
    const fromDirectKeys = fromElements.map(getKey).filter(Boolean);
    const toDirectKeys = toElements.map(getKey).filter(Boolean);
    const directKeysReordered = fromDirectKeys.length >= 2
        && fromDirectKeys.length === toDirectKeys.length
        && new Set(fromDirectKeys).size === fromDirectKeys.length
        && fromDirectKeys.some((key, index) => key !== toDirectKeys[index])
        && fromDirectKeys.every((key) => toDirectKeys.includes(key));
    if (!directKeysReordered) return correspondence;

    const idCounts = (elements: Element[]): Map<string, number> => {
        const counts = new Map<string, number>();
        const count = (element: Element): void => {
            if (element.id) counts.set(element.id, (counts.get(element.id) ?? 0) + 1);
        };
        for (const element of elements) {
            count(element);
            element.querySelectorAll('[id]').forEach(count);
        }
        return counts;
    };
    const fromIdCounts = idCounts(fromElements);
    const toIdCounts = idCounts(toElements);
    const tokens = new Map<string, symbol>();
    for (const [id, count] of fromIdCounts) {
        if (count === 1 && toIdCounts.get(id) === 1) tokens.set(id, Symbol(id));
    }

    const indexDescendant = (element: Element): void => {
        if (getKey(element)) return;
        for (const descendant of element.querySelectorAll('[id]')) {
            const token = tokens.get(descendant.id);
            if (!token) continue;
            correspondence.set(element, token);
            return;
        }
    };
    fromElements.forEach(indexDescendant);
    toElements.forEach(indexDescendant);
    return correspondence;
};

const createContext = (options: MorphOptions = {}) => {
    const context = {
        key: options.key ?? ((element: Element) => element.getAttribute('key')),
        lookahead: options.lookahead ?? false,
        updating: options.updating ?? noop,
        updated: options.updated ?? noop,
        removing: options.removing ?? noop,
        removed: options.removed ?? noop,
        adding: options.adding ?? noop,
        added: options.added ?? noop,
        skipUntilCondition: null as ((node: Node) => boolean) | null,
    };

    const getKey = (node: Node | null): unknown => (
        node instanceof Element ? context.key(node) : null
    );

    const requiresReplacement = (from: Node, to: Node): boolean => (
        from.nodeType !== to.nodeType
        || from.nodeName !== to.nodeName
        || getKey(from) !== getKey(to)
    );

    const insertNodeBefore = (
        parent: Parent | Block,
        source: Node,
        before: Node | null,
    ): {inserted: false; node: null} | {inserted: true; node: AlpineNode} => {
        if (shouldSkip(context.adding, source)) return {inserted: false, node: null};
        const clone = source.cloneNode(true);
        parent.insertBefore(clone, before);
        context.added(clone);
        return {inserted: true, node: clone as AlpineNode};
    };

    const removeNode = (node: Node): boolean => {
        if (shouldSkip(context.removing, node)) return false;
        node.parentNode?.removeChild(node);
        context.removed(node);
        return true;
    };

    const removeOwnedRange = (range: OwnedRange): boolean => {
        if (shouldSkip(context.removing, range.start)) return false;
        range.start.parentNode?.removeChild(range.start);
        context.removed(range.start);
        return true;
    };

    const replaceNode = (from: Node, to: Node): boolean => {
        if (shouldSkip(context.removing, from)) return false;
        const clone = to.cloneNode(true);
        if (shouldSkip(context.adding, clone)) return false;
        from.parentNode?.replaceChild(clone, from);
        context.removed(from);
        context.added(clone);
        return true;
    };

    const patchAttributes = (from: AlpineNode & Element, to: Element): boolean => {
        if (from._x_transitioning) return false;
        if ((from._x_isShown && !(to as AlpineNode)._x_isShown)
            || (!from._x_isShown && (to as AlpineNode)._x_isShown)) return false;

        let changed = false;
        const existing = Array.from(from.attributes);
        for (let index = existing.length - 1; index >= 0; index -= 1) {
            const name = existing[index]!.name;
            if (to.hasAttribute(name)) continue;
            if (name === 'open' && from instanceof HTMLDialogElement && from.open) from.close();
            else from.removeAttribute(name);
            changed = true;
        }
        const incoming = Array.from(to.attributes);
        for (let index = incoming.length - 1; index >= 0; index -= 1) {
            const attribute = incoming[index]!;
            const {name, value} = attribute;
            if (from.getAttribute(name) === value) continue;
            setIncomingAttribute(from, attribute);
            changed = true;
        }
        return changed;
    };

    let patchChildren: (from: Parent | Block, to: Parent | Block) => boolean;

    const patchNode = (from: AlpineNode, to: AlpineNode, pruning: PruningPolicy): boolean => {
        if (from instanceof Element && from.hasAttribute('data-ui-ignore-morph')) return false;
        if (requiresReplacement(from, to)) return replaceNode(from, to);
        if (pruning === 'before-descendant-hook'
            && from.nodeType !== Node.COMMENT_NODE
            && isServerEqual(from, to)) return false;

        let childrenOnly = false;
        let skipChildren = false;
        let skip = false;
        const skipUntil = (predicate: (node: Node) => boolean): void => {
            context.skipUntilCondition = predicate;
        };
        context.updating(
            from,
            to,
            () => { childrenOnly = true; },
            () => { skip = true; },
            () => { skipChildren = true; },
            skipUntil,
        );
        if (skip) return false;

        // Descendants that are server-identical are opaque. The root is checked only
        // after its hook has had the opportunity to mutate the incoming tree.
        if (isServerEqual(from, to)) return false;

        let changed = false;
        if (from.nodeType === Node.ELEMENT_NODE && alpineInstance) {
            alpineInstance.cloneNode(from as Element, to as Element);
            if (from._x_teleport && to._x_teleport) {
                changed = patch(from._x_teleport, to._x_teleport) || changed;
            }
        }

        if (to.nodeType === Node.TEXT_NODE || to.nodeType === Node.COMMENT_NODE) {
            if (from.nodeValue === to.nodeValue) return false;
            from.nodeValue = to.nodeValue;
            context.updated(from, to);
            return true;
        }

        if (!childrenOnly) changed = patchAttributes(from as AlpineNode & Element, to as Element) || changed;
        if (!skipChildren) changed = patchChildren(from as unknown as Parent, to as unknown as Parent) || changed;
        if (changed) context.updated(from, to);
        return changed;
    };

    const patch = (from: AlpineNode, to: AlpineNode): boolean => (
        patchNode(from, to, 'before-descendant-hook')
    );
    const patchRoot = (from: AlpineNode, to: AlpineNode): boolean => (
        patchNode(from, to, 'after-root-hook')
    );

    patchChildren = (from: Parent | Block, to: Parent | Block): boolean => {
        const fromElements = Array.from(from.children);
        const toElements = Array.from(to.children);
        const correspondence = buildDescendantCorrespondence(fromElements, toElements, getKey);

        const childKey = (node: Node | null): unknown => (
            node ? getKey(node) || correspondence.get(node) : null
        );
        const keyed = new Map<unknown, AlpineNode>();
        for (const element of fromElements) {
            const key = childKey(element);
            if (key) keyed.set(key, element as AlpineNode);
        }

        let changed = false;
        let currentFrom = from.firstChild as AlpineNode | null;
        let currentTo = to.firstChild as AlpineNode | null;

        incoming: while (currentTo) {
            seedDynamicId(currentTo, currentFrom);

            if (context.skipUntilCondition) {
                const fromDone = !currentFrom || context.skipUntilCondition(currentFrom);
                const toDone = context.skipUntilCondition(currentTo);
                if (fromDone && toDone) context.skipUntilCondition = null;
                else {
                    if (!fromDone && currentFrom) currentFrom = afterOwnedRange(from, ownedRange(currentFrom));
                    if (!toDone) currentTo = nextSibling(to, currentTo) as AlpineNode | null;
                    continue;
                }
            }

            if (!currentFrom) {
                const addition = insertNodeBefore(from, currentTo, null);
                changed = addition.inserted || changed;
                currentTo = nextSibling(to, currentTo) as AlpineNode | null;
                continue;
            }

            if (isConditionalStart(currentFrom) && isConditionalStart(currentTo)) {
                const fromEnd = conditionalEnd(from, currentFrom);
                const toEnd = conditionalEnd(to, currentTo);
                changed = patchChildren(new Block(currentFrom as Comment, fromEnd), new Block(currentTo as Comment, toEnd)) || changed;
                currentFrom = nextSibling(from, fromEnd) as AlpineNode | null;
                currentTo = nextSibling(to, toEnd) as AlpineNode | null;
                continue;
            } else {
                if (context.lookahead
                    && currentFrom instanceof Element
                    && !currentFrom.isEqualNode(currentTo)) {
                    let lookahead = nextSibling(to, currentTo) as AlpineNode | null;
                    while (lookahead) {
                        if (lookahead instanceof Element && currentFrom.isEqualNode(lookahead)) {
                            const addition = insertNodeBefore(from, currentTo, currentFrom);
                            if (!addition.inserted) {
                                currentTo = nextSibling(to, currentTo) as AlpineNode | null;
                                continue incoming;
                            }
                            currentFrom = addition.node;
                            changed = true;
                            break;
                        }
                        lookahead = nextSibling(to, lookahead) as AlpineNode | null;
                    }
                }

                const toKey = childKey(currentTo);
                const fromKey = childKey(currentFrom);
                if (toKey !== fromKey) {
                    const keyedMatch = toKey ? keyed.get(toKey) : undefined;
                    if (keyedMatch) {
                        moveOwnedRangeBefore(from, ownedRange(keyedMatch), currentFrom);
                        currentFrom = keyedMatch;
                        changed = true;
                    } else if (toKey || fromKey) {
                        const addition = insertNodeBefore(from, currentTo, currentFrom);
                        if (!addition.inserted) {
                            currentTo = nextSibling(to, currentTo) as AlpineNode | null;
                            continue;
                        }
                        currentFrom = addition.node;
                        changed = true;
                    }
                }

                const afterFrom = afterOwnedRange(from, ownedRange(currentFrom));
                changed = patch(currentFrom, currentTo) || changed;
                currentFrom = afterFrom;
            }

            currentTo = nextSibling(to, currentTo) as AlpineNode | null;
        }

        while (currentFrom) {
            const range = ownedRange(currentFrom);
            const after = afterOwnedRange(from, range);
            changed = (range.end === range.start ? removeNode(currentFrom) : removeOwnedRange(range)) || changed;
            currentFrom = after;
        }
        return changed;
    };

    return {patchChildren, patchRoot};
};

const morph = (from: Element, toHtml: Element | string, options: MorphOptions = {}): Element => {
    const to = (typeof toHtml === 'string' ? createElement(toHtml) : toHtml) as AlpineNode & Element;
    const source = from as AlpineNode & Element;
    if (alpineInstance?.closestDataStack && !source._x_dataStack) {
        to._x_dataStack = alpineInstance.closestDataStack(source);
        if (to._x_dataStack) alpineInstance.cloneNode(source, to);
    }
    createContext(options).patchRoot(source, to);
    return from;
};

const morphBetween = (
    startMarker: Comment,
    endMarker: Comment,
    toHtml: Element | string,
    options: MorphOptions = {},
): void => {
    const fromContainer = startMarker.parentNode as AlpineNode & Parent;
    const toContainer = typeof toHtml === 'string'
        ? (() => {
            const container = document.createElement('div');
            container.insertAdjacentHTML('beforeend', toHtml);
            return container;
        })()
        : toHtml;
    const toStart = document.createComment('[morph-start]');
    const toEnd = document.createComment('[morph-end]');
    toContainer.insertBefore(toStart, toContainer.firstChild);
    toContainer.appendChild(toEnd);
    if (alpineInstance?.closestDataStack) {
        (toContainer as AlpineNode)._x_dataStack = alpineInstance.closestDataStack(fromContainer);
        if ((toContainer as AlpineNode)._x_dataStack) alpineInstance.cloneNode(fromContainer as unknown as Element, toContainer);
    }
    createContext(options).patchChildren(
        new Block(startMarker, endMarker),
        new Block(toStart, toEnd),
    );
};

const trellisMorph = (AlpineInstance: Alpine.Alpine): void => {
    const instance = AlpineInstance as MorphAlpine;
    alpineInstance = instance;
    instance.morph = morph;
    instance.morphBetween = morphBetween;
};

export {morph, morphBetween};
export default trellisMorph;
